import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import { ProductCard } from "@/features/instantsearch/components";
import { singleInstantSearch } from "@/features/instantsearch";
import { ProductShowcase } from "./product-showcase";
import type { InstantSearchResult } from "@/features/instantsearch/types/instantsearch-types";
import { useTranslation } from "react-i18next";
import useUIPreferencesStore from "@/store/use-ui-preferences-store";
import {
  buildProductCarouselQuery,
  productDiscoverPath,
  orderProductCarouselHits,
  productCarouselLoadDecision,
  selectProductCarouselHits,
  type ProductCarouselQueryResult,
} from "./product-carousel-query";

export interface ProductCarouselWidgetProps {
  discoverQuery?: string;
  className?: string;
  productIds?: string[]; // 🆕 Product ID listesi - AI response'dan gelen ürün ID'leri
  offerMap?: Record<string, string>;
  filterOptions?: Record<string, string[]>; // 🆕 VectorSearch'ten gelen filterOptions
}

export const ProductCarouselWidget: React.FC<ProductCarouselWidgetProps> = ({
  className,
  productIds,
  offerMap,
  filterOptions,
  discoverQuery,
}) => {
  const { t } = useTranslation();
  const language = useUIPreferencesStore((state) => state.language);
  // The plan key carries the language so a language switch refetches (one
  // merged collection serves all languages).
  const plan = useMemo(
    () =>
      productIds && productIds.length > 0
        ? buildProductCarouselQuery(productIds, offerMap, language)
        : undefined,
    [language, offerMap, productIds],
  );
  const [loadedResult, setLoadedResult] = useState<
    ProductCarouselQueryResult<InstantSearchResult> | undefined
  >();
  const products = selectProductCarouselHits(plan?.key, loadedResult);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedKeyRef = useRef<string | undefined>(undefined);

  // Fetch products - Product ID'leri ile filtreleme
  useEffect(() => {
    if (!plan) return; // Product ID'ler yoksa hiçbir şey yapma
    const load = productCarouselLoadDecision(plan.key, fetchedKeyRef.current);
    setIsLoading(load.loading);
    if (!load.shouldFetch) return;
    const controller = new AbortController();
    let active = true;

    // ✅ Product ID'leri ile filtreleme
    singleInstantSearch<InstantSearchResult>({
      ...plan.request,
      query: "*",
      language,
      signal: controller.signal,
    })
      .then((res) => {
        if (!active) return;
        fetchedKeyRef.current = plan.key;
        const orderedHits = orderProductCarouselHits(res?.hits || [], plan);
        setLoadedResult({ key: plan.key, hits: orderedHits });
      })
      .catch((error) => {
        if (active && !controller.signal.aborted) {
          console.error("Product fetch error:", error);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [language, plan]);

  if (!isLoading && products.length === 0) {
    return null;
  }

  return (
    <ProductShowcase
      className={className}
      loading={isLoading}
      items={products}
      getKey={(product, index) =>
        product.id ||
        product.document?.id ||
        product.productID?.toString() ||
        `product-${index}`
      }
      renderItem={(product) => <ProductCard product={product} />}
      action={
        <Link
          to={productDiscoverPath(filterOptions, discoverQuery)}
          state={{ filters: filterOptions || {}, fromChat: true }}
          className="flex items-center gap-2 rounded-lg border border-border-primary bg-white px-3 py-2 transition-colors hover:bg-bg-secondary dark:border-border-primary dark:bg-bg-secondary"
        >
          <Search
            className="h-3.5 w-3.5 text-text-tertiary dark:text-text-tertiary"
            strokeWidth={2}
          />
          <span className="text-xs font-semibold text-text-secondary dark:text-text-secondary sm:text-sm">
            {t("shopping.discoverMore")}
          </span>
          <ChevronRight
            className="h-3.5 w-3.5 text-text-tertiary dark:text-text-tertiary"
            strokeWidth={2}
          />
        </Link>
      }
    />
  );
};
